import classes from './PriceTable.module.css';
import Icon from 'Components/Atoms/Icon/Icon';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import iconInfinite from './assets/icon-infinite.svg';
import { PLANS } from 'Utils/constants';
import { useTranslation } from 'react-i18next';
import useLocale from 'Hooks/useLocale';

const PRICES = {
    BUSINESS: {
        STARTER: {
            monthly: 59,
            yearly: 42,
            yearlyFullPrice: 499,
            savings: 209,
        },
        ADVANCED: {
            monthly: 119,
            yearly: 83,
            yearlyFullPrice: 999,
            savings: 429,
        },
        PREMIUM: {
            monthly: null,
            yearly: null,
            yearlyFullPrice: null,
            savings: null,
        },
    },
};

function formatList(list, locale) {
    if (typeof Intl.ListFormat === 'undefined') {
        return list.join(', ');
    }

    const formatter = new Intl.ListFormat(locale, {
        style: 'long',
        type: 'conjunction',
    });
    return formatter.format(list);
}

function formatDisjunctiveList(list, locale) {
    if (typeof Intl.ListFormat === 'undefined') {
        const disjuntive = locale === 'es-ES' ? 'o' : 'or';

        return list.join(` ${disjuntive} `);
    }

    const formatter = new Intl.ListFormat(locale, {
        style: 'long',
        type: 'disjunction',
    });
    return formatter.format(list);
}

export default function usePriceTableConfig() {
    const { t } = useTranslation('pricing');
    const locale = useLocale();

    const priceConfig = {
        plans: [
            {
                id: PLANS.BUSINESS_2,
                title: t('business-starter.title'),
                description: (
                    <>
                        {t('business-starter.description-slot-1')}{' '}
                        <Tooltip label={t('business-starter.billing-tooltip')}>
                            <strong className={classes.highlight} style={{ cursor: 'help' }}>
                                {t('business-starter.description-slot-2')}
                            </strong>
                        </Tooltip>
                    </>
                ),
                classes: {
                    title: classes.gradientTextPrimary,
                },
                price: PRICES.BUSINESS.STARTER,
                includesLabel: null,
                iconList: [
                    {
                        iconName: 'dashboards',
                        title: t('business-starter.highlights.dashboards'),
                        description: t('business-starter.highlights.dashboards-description'),
                    },
                    {
                        iconName: 'banks',
                        title: t('business-starter.highlights.banks'),
                        description: t('business-starter.highlights.banks-description'),
                    },
                    {
                        iconName: 'invoices',
                        title: t('business-starter.highlights.invoices'),
                        description: t('business-starter.highlights.invoices-description'),
                    },
                    {
                        iconName: 'users',
                        title: t('business-starter.highlights.users'),
                        description: t('business-starter.highlights.users-description'),
                    },
                    {
                        iconName: 'societies',
                        title: t('business-starter.highlights.company'),
                        description: t('business-starter.highlights.company-description'),
                    },
                    {
                        iconName: 'categories',
                        title: t('business-starter.highlights.categorization'),
                        description: t('business-starter.highlights.categorization-description'),
                    },
                    {
                        iconName: 'reconciliation',
                        title: t('business-starter.highlights.reconciliation'),
                        description: t('business-starter.highlights.reconciliation-description'),
                    },
                    {
                        iconName: 'alerts',
                        title: t('business-starter.highlights.alerts'),
                        description: t('business-starter.highlights.alerts-description'),
                    },
                ],
            },
            {
                id: PLANS.BUSINESS_3,
                title: t('business-advanced.title'),
                description: (
                    <>
                        {t('business-advanced.description-slot-1')}{' '}
                        <Tooltip label={t('business-advanced.billing-tooltip')}>
                            <strong className={classes.highlight} style={{ cursor: 'help' }}>
                                {t('business-advanced.description-slot-2')}
                            </strong>
                        </Tooltip>
                    </>
                ),
                classes: {
                    title: classes.gradientTextBlack,
                },
                price: PRICES.BUSINESS.ADVANCED,
                includesLabel: t('business-advanced.highlights.includes-starter'),
                iconList: [
                    {
                        iconName: 'banks',
                        title: t('business-advanced.highlights.banks'),
                        description: t('business-advanced.highlights.banks-description'),
                    },
                    {
                        iconName: 'invoices',
                        title: t('business-advanced.highlights.invoices'),
                        description: t('business-advanced.highlights.invoices-description'),
                    },
                    {
                        iconName: 'users',
                        title: t('business-advanced.highlights.users'),
                        description: t('business-advanced.highlights.users-description'),
                    },
                    {
                        iconName: 'societies',
                        title: t('business-advanced.highlights.company'),
                        description: t('business-advanced.highlights.company-description'),
                    },
                    {
                        iconName: 'forecast',
                        title: t('business-advanced.highlights.forecasting'),
                        description: t('business-advanced.highlights.forecasting-description'),
                    },
                    {
                        iconName: 'integrations',
                        title: t('business-advanced.highlights.erp'),
                        description: t('business-advanced.highlights.erp-description'),
                    },
                    {
                        iconName: 'chat',
                        title: t('business-advanced.highlights.support'),
                        description: t('business-advanced.highlights.support-description'),
                    },
                ],
            },
        ],
        table: {
            columns: [
                {
                    title: <div className={classes.gradientTextPrimary}>{t('business-starter.title')}</div>,
                    price: PRICES.BUSINESS.STARTER,
                },
                {
                    title: <div className={classes.gradientTextBlack}>{t('business-advanced.title')}</div>,
                    price: PRICES.BUSINESS.ADVANCED,
                },
                {
                    title: <div className={classes.gradientTextTertiary}>{t('business-premium.title')}</div>,
                    price: t('personalized'),
                },
            ],
            rows: [
                {
                    isDivider: true,
                    title: t('cross-table.connected-banks'),
                },
                {
                    title: t('cross-table.syncronized-banks'),
                    cells: ['5', '10', t('personalized')],
                },
                {
                    title: t('cross-table.billing'),
                    cells: [
                        t('business-starter.billing-limit'),
                        t('business-advanced.billing-limit'),
                        t('business-premium.billing-limit'),
                    ],
                },
                {
                    isDivider: true,
                    title: t('cross-table.categorization'),
                },
                {
                    title: t('cross-table.categorization-suggestions'),
                    cells: [
                        <div className="flex justify-center items-center" key="categorization-suggestions-check-0">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="categorization-suggestions-check-1">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="categorization-suggestions-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.automatic-rules'),
                    cells: [
                        <div className="flex justify-center items-center" key="automatic-rules-infinite-0">
                            <img src={iconInfinite} alt={t('cross-table.unlimited')} />
                        </div>,
                        <div className="flex justify-center items-center" key="automatic-rules-infinite-1">
                            <img src={iconInfinite} alt={t('cross-table.unlimited')} />
                        </div>,
                        <div className="flex justify-center items-center" key="automatic-rules-infinite-2">
                            <img src={iconInfinite} alt={t('cross-table.unlimited')} />
                        </div>,
                    ],
                },
                {
                    isDivider: true,
                    title: t('cross-table.search'),
                },
                {
                    title: t('cross-table.history'),
                    cells: [
                        <div className="flex justify-center items-center" key="history-infinite-0">
                            <img src={iconInfinite} alt={t('cross-table.unlimited')} />
                        </div>,
                        <div className="flex justify-center items-center" key="history-infinite-1">
                            <img src={iconInfinite} alt={t('cross-table.unlimited')} />
                        </div>,
                        <div className="flex justify-center items-center" key="history-infinite-2">
                            <img src={iconInfinite} alt={t('cross-table.unlimited')} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.export'),
                    cells: [
                        formatList(['PDF', 'CSV', 'JSON'], locale),
                        formatList(['PDF', 'CSV', 'JSON'], locale),
                        formatList(['PDF', 'CSV', 'JSON'], locale),
                    ],
                },
                {
                    title: t('cross-table.import'),
                    cells: [
                        t('cross-table.self-service'),
                        t('cross-table.self-service'),
                        <div className="flex items-center gap-1 justify-center" key="importacion-datos-plus">
                            <span>{t('cross-table.ocr')}</span>
                            <Tooltip label={t('cross-table.ocr-description')}>
                                <span className="cursor-help inline-flex">
                                    <Icon name="circle-info" size={20} color="rgba(32,32,32, .25)" />
                                </span>
                            </Tooltip>
                        </div>,
                    ],
                },
                {
                    isDivider: true,
                    title: t('cross-table.cash-flow'),
                },
                {
                    title: t('cross-table.dashboards'),
                    cells: [
                        <div className="flex justify-center items-center" key="dashboards-check-0">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="dashboards-check-1">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="dashboards-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.reports-alerts'),
                    cells: [
                        <div className="flex justify-center items-center" key="reports-alerts-check-0">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="reports-alerts-check-1">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="reports-alerts-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.forecasting'),
                    cells: [
                        <div className="flex justify-center items-center" key="forecasting-close-0">
                            <Icon name="close" color="#cccccc" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="forecasting-check-1">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="forecasting-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.conciliation'),
                    cells: [
                        <div className="flex justify-center items-center" key="conciliation-close-0">
                            <Icon name="close" color="#cccccc" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="conciliation-close-1">
                            <Icon name="close" color="#cccccc" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="conciliation-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    isDivider: true,
                    title: t('cross-table.concilation'),
                },
                {
                    title: t('cross-table.invoices'),
                    cells: [
                        <div className="flex justify-center items-center" key="invoices-check-0">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="invoices-check-1">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="invoices-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.contacts'),
                    cells: [
                        <div className="flex justify-center items-center" key="contacts-check-0">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="contacts-check-1">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="contacts-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.invoices-quarter'),
                    cells: ['250', '1000', t('personalized')],
                },
                {
                    isDivider: true,
                    title: t('cross-table.integrations'),
                },
                {
                    title: t('cross-table.messaging-channels'),
                    cells: [
                        <div className="flex justify-center items-center" key="messaging-channels-check-0">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="messaging-channels-check-1">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="messaging-channels-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.erps'),
                    cells: [
                        <div className="flex justify-center items-center" key="erps-close-0">
                            <Icon name="close" color="#cccccc" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="erps-check-1">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="erps-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    isDivider: true,
                    title: t('cross-table.multi-users'),
                },
                {
                    title: t('cross-table.users'),
                    cells: ['3', '5', t('personalized')],
                },
                {
                    title: t('cross-table.number-of-companies'),
                    cells: ['1', '1', t('personalized')],
                },
                {
                    isDivider: true,
                    title: t('cross-table.support'),
                },
                {
                    title: t('cross-table.account-manager'),
                    cells: [
                        <div className="flex justify-center items-center" key="account-manager-close-0">
                            <Icon name="close" color="#cccccc" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="account-manager-close-1">
                            <Icon name="close" color="#cccccc" size={14} />
                        </div>,
                        <div className="flex justify-center items-center" key="account-manager-check-2">
                            <Icon name="check" color="primary" size={14} />
                        </div>,
                    ],
                },
                {
                    title: t('cross-table.contact-methods'),
                    cells: [
                        formatDisjunctiveList(['Chat', 'Email'], locale),
                        formatDisjunctiveList(['Chat', 'Email'], locale),
                        formatDisjunctiveList(['Chat', 'Email', t('cross-table.phone')], locale),
                    ],
                },
            ],
        },
    };

    return priceConfig;
}
